import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api, parsePaginatedResponse } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { useEntitlements } from '../../hooks/useEntitlements';
import { Link, useNavigate } from 'react-router-dom';

interface Campaign {
    id: string;
    name: string;
    slug: string;
    status: string;
    description?: string;
    start_at?: string;
    end_at?: string;
    created_at?: string;
    game_summary?: {
        count: number;
        label: string;
        firstGameId?: string;
    };
    gift_count?: number;
    player_count?: number;
    play_count?: number;
    today_play_count?: number;
    daily_rate?: number;
}

export default function CampaignsList() {
    const navigate = useNavigate();
    const { entitlements, refresh: refreshEntitlements } = useEntitlements();
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [nonArchivedTotal, setNonArchivedTotal] = useState(0);
    const [error, setError] = useState('');
    const { user } = useAuthStore();
    const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant_id || '';

    // Upgrade and Renewal states
    const [wallet, setWallet] = useState<any>(null);
    const [showLimitModal, setShowLimitModal] = useState(false);

    const fetchWallet = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const res = await fetch('/api/admin/wallet', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setWallet(data.data ?? null);
            }
        } catch (e) {
            console.error('Lỗi lấy ví:', e);
        }
    };

    const fetchCampaigns = async () => {
        if (!tenantId) return;  // Chờ user load xong
        try {
            setLoading(true);
            setError('');
            const data = await api.getCampaigns(tenantId, page, 20);
            const { items, total: responseTotal } = parsePaginatedResponse<Campaign>(data);
            setCampaigns(items);
            setTotal(responseTotal);
            setNonArchivedTotal(data?.non_archived_total ?? responseTotal);
        } catch (err: any) {
            console.error('Failed to fetch campaigns:', err);
            setError(err.message || 'Không tải được danh sách chiến dịch');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
        fetchWallet();
    }, [page, tenantId]);

    const handleDelete = async (id: string, name: string) => {
        if (!confirm(`Bạn có chắc muốn xóa "${name}"? Thao tác này không thể hoàn tác.`)) return;
        try {
            await api.deleteCampaign(id);
            setCampaigns((previous) => previous.filter((campaign) => campaign.id !== id));
        } catch (err: any) {
            console.error('Failed to delete:', err);
            alert(err.message || 'Failed to delete campaign');
        }
    };

    const statusColors: Record<string, string> = {
        draft: 'bg-gray-100 text-gray-800',
        configured: 'bg-blue-100 text-blue-800',
        scheduled: 'bg-purple-100 text-purple-800',
        active: 'bg-green-100 text-green-800',
        paused: 'bg-yellow-100 text-yellow-800',
        ended: 'bg-red-100 text-red-800',
        archived: 'bg-gray-100 text-gray-600',
    };

    const statusLabels: Record<string, string> = {
        draft: 'Bản nháp',
        configured: 'Đã cấu hình',
        scheduled: 'Đã lên lịch',
        active: 'Đang chạy',
        paused: 'Tạm dừng',
        ended: 'Đã kết thúc',
        archived: 'Lưu trữ',
    };

    return (
        <Layout>
            <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Chiến dịch</h1>
                    <p className="mt-1 text-gray-500">Tình trạng chiến dịch, trò chơi, kho quà, dữ liệu lead và báo cáo</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-bold text-white bg-indigo-600`}>
                            💎 {entitlements?.plan_label || 'Đang tải...'}
                        </span>
                        <span className="font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
                            👛 Ví xu: {wallet !== null ? `${wallet.balance} xu` : '...'}
                        </span>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            window.location.href = '/campaigns/new';
                        }}
                        className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 font-semibold cursor-pointer text-sm"
                    >
                        + Chiến dịch mới
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="flex h-64 items-center justify-center">
                    <div className="text-gray-500">Đang tải...</div>
                </div>
            ) : (
                <>
                    {campaigns.length === 0 ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
                            <div className="mb-4 text-6xl">📢</div>
                            <h3 className="mb-2 text-xl font-semibold text-gray-900">Chưa có chiến dịch nào</h3>
                            <p className="text-gray-500">Tạo chiến dịch đầu tiên để bắt đầu.</p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Tên</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Trò chơi</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Quà tặng</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Người chơi</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Slug</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Trạng thái</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Phí chạy</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Ngày bắt đầu</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Ngày kết thúc</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Hành động</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {campaigns.map((campaign) => (
                                        <tr key={campaign.id} className="hover:bg-gray-50">
                                            <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                                                <a href={`/campaigns/${campaign.id}`} className="hover:text-blue-600">
                                                    {campaign.name}
                                                </a>
                                                {campaign.description && !campaign.description.includes('Warning:') && (
                                                    <div className="max-w-xs truncate text-xs font-normal text-gray-400 mt-0.5">
                                                        {campaign.description.slice(0, 60)}{campaign.description.length > 60 ? '…' : ''}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                                                {campaign.game_summary?.count ? (
                                                    <div>
                                                        <div className="font-medium text-gray-900">{campaign.game_summary.label}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {campaign.game_summary.count} trò chơi
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">Chưa có</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                {campaign.gift_count || 0} quà
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                <div>{campaign.player_count || 0} người</div>
                                                {(() => {
                                                    const limit = entitlements?.features?.max_plays_month && entitlements?.features?.max_plays_month !== -1
                                                        ? entitlements.features.max_plays_month
                                                        : (entitlements?.features?.max_plays_day || 0);
                                                    
                                                    if (!limit) return <div className="text-xs text-gray-400">{campaign.play_count || 0} lượt</div>;
                                                    
                                                    const currentPlays = entitlements?.features?.max_plays_month !== -1
                                                        ? (campaign.play_count || 0)
                                                        : (campaign.today_play_count || 0);

                                                    const pct = Math.min(100, Math.round((currentPlays / limit) * 100));
                                                    const isNearLimit = pct >= 80;

                                                    return (
                                                        <div className="mt-1.5 w-36">
                                                            <div className="flex justify-between items-center text-[10px] font-semibold mb-0.5">
                                                                <span className={isNearLimit ? 'text-amber-600' : 'text-gray-500'}>
                                                                    {currentPlays}/{limit} lượt {entitlements?.features?.max_plays_month !== -1 ? '(tháng)' : '(ngày)'}
                                                                </span>
                                                                <span className={isNearLimit ? 'text-amber-600 font-bold' : 'text-gray-400'}>{pct}%</span>
                                                            </div>
                                                            <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden border border-gray-200/50">
                                                                <div 
                                                                    className={`h-full rounded-full transition-all duration-500 ${isNearLimit ? 'bg-gradient-to-r from-amber-500 to-orange-500 shadow-[0_0_4px_#f59e0b]' : 'bg-gradient-to-r from-blue-500 to-indigo-500'}`} 
                                                                    style={{ width: `${pct}%` }}
                                                                />
                                                            </div>
                                                            {isNearLimit && (
                                                                <a 
                                                                    href="/billing" 
                                                                    className="inline-flex items-center gap-0.5 mt-1 px-2 py-0.5 bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 border border-orange-200 rounded text-[9px] font-black text-orange-600 transition-all uppercase tracking-wider shadow-sm"
                                                                >
                                                                    ⚡ Tăng hạn mức
                                                                </a>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{campaign.slug}</td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColors[campaign.status] || 'bg-gray-100 text-gray-800'}`}>
                                                    {statusLabels[campaign.status] || campaign.status}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-indigo-600">
                                                {campaign.daily_rate !== undefined ? `${campaign.daily_rate} xu/ngày` : '0 xu/ngày'}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                {campaign.start_at ? new Date(campaign.start_at).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                {campaign.end_at ? new Date(campaign.end_at).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                <a href={`/campaigns/${campaign.id}`} className="mr-3 font-medium text-blue-600 hover:text-blue-800">Quản lý</a>
                                                {campaign.game_summary?.firstGameId && campaign.status === 'active' && (
                                                    <button
                                                        onClick={() => window.open(`/preview/instance/${campaign.game_summary?.firstGameId}`, '_blank')}
                                                        className="mr-3 text-slate-700 hover:text-slate-900 cursor-pointer"
                                                    >
                                                        Xem trước
                                                    </button>
                                                )}
                                                {campaign.game_summary?.firstGameId && campaign.status !== 'active' && (
                                                    <span title="Chỉ chiến dịch Đang chạy mới mở preview public thật." className="mr-3 text-gray-400">
                                                        Preview khóa
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {total > 20 && (
                        <div className="mt-4 flex items-center justify-between">
                            <button
                                onClick={() => setPage((current) => Math.max(1, current - 1))}
                                disabled={page === 1}
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 disabled:opacity-50"
                            >
                                Trước
                            </button>
                            <span className="text-sm text-gray-500">Trang {page}</span>
                            <button
                                onClick={() => setPage((current) => current + 1)}
                                disabled={page * 20 >= total}
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 disabled:opacity-50"
                            >
                                Tiếp
                            </button>
                        </div>
                    )}
                </>
            )}
            {showLimitModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-150 text-center">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500 text-3xl shadow-inner animate-pulse">
                            ⚠️
                        </div>
                        <h3 className="mt-4 text-xl font-bold text-gray-900">Đã đạt giới hạn chiến dịch</h3>
                        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                            Bạn đã dùng hết giới hạn tạo chiến dịch của gói{' '}
                            <strong className="text-gray-900">{entitlements?.plan_label || 'Dùng thử'}</strong> (
                            {nonArchivedTotal}/
                            {entitlements?.features?.max_campaigns || 1}).
                        </p>
                        <p className="mt-2 text-xs text-gray-400">Hãy lưu trữ (archive) bớt chiến dịch cũ hoặc nâng cấp gói cước mới để chạy song song nhiều chiến dịch cùng lúc.</p>
                        <div className="mt-6 flex flex-col gap-2">
                            <Link
                                to="/billing"
                                className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 py-3 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all text-center cursor-pointer"
                            >
                                🚀 Nâng cấp gói ngay
                            </Link>
                            <button
                                onClick={() => setShowLimitModal(false)}
                                className="w-full rounded-xl border border-gray-300 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition cursor-pointer"
                            >
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
